import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { assign } from 'lodash';
import { finalize } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ReportManagementService } from 'src/app/common/services/report-management.service';
import { AuthenticationService } from 'src/app/common/services/authentication.service';
import { ConfigurationService } from 'src/app/common/services/configuration.service';
import { LoaderService } from 'src/app/common/services/loader.service';
import { AlertMessageService } from 'src/app/common/services/alert-message.service';
import { is } from 'date-fns/locale';

@Component({
  selector: 'app-report-management',
  templateUrl: './report-management.component.html',
  styleUrls: ['./report-management.component.css']
})
export class ReportManagementComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  /** Debounced search Subject */
  private searchSubject: Subject<string> = new Subject<string>();
  private searchSubscription?: Subscription;


  @Input() excelExport = false;
  @Input() helpGuide = false;
  @Input() pptExport = false;
  @Input() repository = false;
  @Input() exportDownloads = false;

  ActiveModule1 = false;
  ActiveModule2 = false;
  ActiveModule3 = true;

  searchQuery = '';
  selectedModule = '';
  selectedStatus = '';
  selectedAssignedTo = '';

  // Multi-select filter arrays
  selectedModules: string[] = [];
  selectedStatuses: string[] = [];
  selectedAssignees: string[] = [];

  // Filter dropdowns state
  showModuleDropdown = false;
  showStatusDropdown = false;
  showAssigneeDropdown = false;

  incidents: any[] = [];
  filteredIncidents: any[] = [];

  allModules: string[] = [];
  
  uniqueStatuses: string[] = [];
  uniqueAssignees: string[] = [];

  // Removed loading flags, using LoaderService
  errorMsg = '';

  // Pagination
  currentPage = 1;
  // pageSize = 10;
  pageSize = this.getPageSizeForWidth(typeof window !== 'undefined' ? window.innerWidth : 1024);
  totalRecords = 0;
  totalPages = 1;
  private resizeHandler?: () => void;

  showIncidentPopup = false;
  selectedIncident: any = null;

  // Selection helpers
  isAllSelected = false;
  isIndeterminate = false;
  hasSelectedIncidents = false;

  // Maximum length for hover-over text
  maxLength: number = 20;

  constructor(
    private reportService: ReportManagementService,
    private authenticationService: AuthenticationService,
    private configurationService: ConfigurationService,
    private loaderService: LoaderService,
    private alertService: AlertMessageService
  ) {}


  ngOnInit(): void {
    this.fetchReports();
    this.fetchAllModules(); // Only call this for modules
    this.loadFilterOptions();
    
    // Debounced search subscription
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe((searchTerm: string) => {
      this.currentPage = 1;
      this.fetchReports(searchTerm.trim(), this.currentPage);
    });
    if (typeof window !== 'undefined') {
     this.pageSize = this.getPageSizeForWidth(window.innerWidth);
      this.resizeHandler = () => {
        const newSize = this.getPageSizeForWidth(window.innerWidth);
        if (newSize !== this.pageSize) {
          this.pageSize = newSize;
          this.currentPage = 1;
          this.fetchReports(this.searchQuery);
        }
      };
      window.addEventListener('resize', this.resizeHandler);
    }
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.resizeHandler && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeHandler);
    }

  }

  fetchAllModules(): void {
    this.configurationService.getModuleName().subscribe((result: any) => {
      if (result && result.HasValidData) {
        try {
          const modules = JSON.parse(result.Data);
          // If modules is an array of objects, map to names
          this.allModules = Array.isArray(modules)
            ? modules.map((m: any) => m.module_name || m.ModuleName || m.name || m)
            : [];
          
          // Add Landing Page to module filter - check both cases
          const landingPageVariants = [ 'Landing Page',];
          const hasLandingPage = landingPageVariants.some(variant => 
            this.allModules.includes(variant)
          );
          
          if (!hasLandingPage) {
            // Check if other modules are uppercase and match that format
            const hasUppercaseModules = this.allModules.some(module => 
              module === module.toUpperCase() && module.includes(' ')
            );
            
            const moduleToAdd = hasUppercaseModules ? 'LANDING PAGE' : 'Landing Page';
            this.allModules.unshift(moduleToAdd); // Add at top
          }
        } catch (e) {
          this.allModules = [];
        }
      }
    });
  }



  /** Only update uniqueAssignees from API, not statuses or modules. */
  loadFilterOptions(): void {
    this.reportService.getFilterOptions().subscribe({
      next: (response) => {
        try {
          const parsed = response?.Data ? JSON.parse(response.Data) : null;
          if (parsed) {
            this.uniqueAssignees = parsed.assignees || [];
          }
        } catch (error) {
          console.error('Error parsing filter options:', error);
        }
      },
      error: (error) => {
        console.error('Error loading filter options:', error);
      }
    });
  }
  /** Fetch reports from API */
  fetchReports(searchTerm: string = '', page: number = 1, append: boolean = false): void {
    this.isLoading = true;
    // Convert selected modules to uppercase when sending to API
    const uppercaseModules = this.selectedModules.map(module => module.toUpperCase());
    
    // Pass date range as filters to the API
    this.reportService
      .getAllReports(
        searchTerm, 
        page, 
        this.pageSize,
        uppercaseModules,
        this.selectedStatuses,
        this.selectedAssignees,
        this.startDate || undefined, // startDate filter
        this.endDate || undefined    // endDate filter
      )
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe({
        next: (res) => {
          try {
            const parsed = res?.Data ? JSON.parse(res.Data) : null;
            if (parsed && parsed.Records) {
              this.totalRecords = parsed.TotalRecords || 0;
              this.pageSize = parsed.PageSize || 10;
              this.totalPages = parsed.TotalPages || 1;

              const newRecords = parsed.Records.map((item: any, index: number) => {
                let status = (item.status || '').toString().trim();
                if (!status || status.toLowerCase() === 'n/a') status = 'New';
                else if (status.toLowerCase() === 'in progress') status = 'In Progress';
                else if (status.toLowerCase() === 'completed') status = 'Completed';
                else if (status.toLowerCase() === 'pending') status = 'Pending';
                else if (status.toLowerCase() === 'closed') status = 'Closed';
                else if (status.toLowerCase() === 'open') status = 'Open';

                const assignedToValue = item.assignedTo || '';
                
                // Handle clientStudy display logic
                let clientStudyDisplay = 'N/A';
                const clientName = item.client_name || item.clientName || '';
                const studyName = item.study_name || item.studyName || '';
                
                if (clientName && studyName) {
                  // If both client name and study name are available, show clientname_studyname
                  clientStudyDisplay = `${clientName}_${studyName}`;
                } else if (clientName) {
                  // If only client name is available, show just client name
                  clientStudyDisplay = clientName;
                } else if (studyName) {
                  // If only study name is available, show just study name
                  clientStudyDisplay = studyName;
                }

                return {
                  id: item.id || item._id || '', // ✅ real document ID
                 issueNo: item.IncidentNo || '',
                  clientStudy: clientStudyDisplay,
                  module: item.module_name || 'N/A',
                  reportedBy: item.email || 'Unknown',
                  reportTitle: item.title || 'No Title',
                  dateTime: this.formatDate(item.creationDate),
                  status: status,
                  assignedTo: assignedToValue,
                  previousAssignedTo: assignedToValue, // Track previous value
                  experience: item.description || '',
                  selectionSummary: item.selectionSummary || item.selection_summary || null, // Handle null explicitly
                  hasAttachment: !!item.attachmentUrl,
                  attachmentUrl: item.attachmentUrl || null,
                  isSelected: false
                  
                };
              });

              this.incidents = append ? [...this.incidents, ...newRecords] : newRecords;
              this.updateUniqueValues();
              this.updateFilteredIncidents();
            } else {
              this.incidents = [];
              this.filteredIncidents = [];
              this.totalRecords = 0;
            }
          } catch (error) {
            console.error('Error parsing API response:', error);
            this.errorMsg = 'Error processing data.';
          }
        },
        error: (err) => {
          console.error('Error fetching reports:', err);
          this.errorMsg = 'Failed to load reports.';
        }
      });
  }

  /** AM/PM Date Formatter */
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    let date: Date;
    
    // Handle '01/11/2025 09:11 AM' format from Elasticsearch
    if (/\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} [AP]M/.test(dateString)) {
      const match = dateString.match(/(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}) ([AP]M)/);
      if (match) {
        let [_, day, month, year, hour, minute, ampm] = match;
        let hourNum = parseInt(hour, 10);
        
        // Convert 12-hour to 24-hour format
        if (ampm === 'PM' && hourNum !== 12) hourNum += 12;
        if (ampm === 'AM' && hourNum === 12) hourNum = 0;
        
        // Create date object (month is 0-indexed in JavaScript)
        date = new Date(Number(year), Number(month) - 1, Number(day), hourNum, Number(minute));
      } else {
        date = new Date(dateString);
      }
    } else {
      // Fallback to standard date parsing
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return dateString;
    
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    };
    return date.toLocaleString('en-US', options);
  }


  /** 🔍 Debounced search input handler */
  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  /** Infinite scroll */
  onScroll(event: any): void {
    const element = event.target;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    
    // Check if we can load more records: not at last page
    const canLoadMore = this.currentPage < this.totalPages;
    
    if (atBottom && canLoadMore) {
      this.currentPage++;
      this.isLoading = true;
      this.fetchReports(this.searchQuery, this.currentPage, true);
    }
  }

  /** ✅ Filter incidents based on dropdowns */
  updateFilteredIncidents(): void {
    const sm = this.selectedModule?.toLowerCase() || '';
    const ss = this.selectedStatus?.toLowerCase() || '';
    const sa = this.selectedAssignedTo?.toLowerCase() || '';

    this.filteredIncidents = this.incidents.filter((incident) => {
      const m = !sm || incident.module?.toLowerCase() === sm;
      const s = !ss || incident.status?.toLowerCase() === ss;
      const a = !sa || incident.assignedTo?.toLowerCase() === sa;
      return m && s && a;
    });
  }


  /** Always show all possible statuses in the status filter, not just those present. */
  updateUniqueValues(): void {
    this.uniqueStatuses = [
      'Client Education',
      'New',
      'Work in Progress',
      'Closed',
    ];
  }

  /** Popup controls */
  openIncidentPopup(incident: any): void {
    this.selectedIncident = incident;
    this.showIncidentPopup = true;
  }
  closeIncidentPopup(): void {
    this.showIncidentPopup = false;
    this.selectedIncident = null;
  }

  /** Helpers */
  getModuleBadgeClass(module: string): string {
    if (!module) return '';
    const key = module.toLowerCase();
    if (key.includes('data')) return 'badge-data';
    if (key.includes('report')) return 'badge-report';
    if (key.includes('client')) return 'badge-client';
    return 'badge-default';
  }
  toggleSelectAll(): void {
    this.isAllSelected = !this.isAllSelected;
    this.filteredIncidents.forEach(i => i.isSelected = this.isAllSelected);
    this.updateSelectionState();
  }

  onIncidentSelectionChange(): void {
    this.updateSelectionState();
  }

  clearSelectedIncidents(): void {
    this.filteredIncidents.forEach(i => (i.isSelected = false));
    this.isAllSelected = false;
    this.isIndeterminate = false;
    this.hasSelectedIncidents = false;
  }

  updateSelectionState(): void {
    const selectedCount = this.filteredIncidents.filter(i => i.isSelected).length;
    this.isAllSelected = selectedCount === this.filteredIncidents.length;
    this.isIndeterminate = selectedCount > 0 && selectedCount < this.filteredIncidents.length;
    this.hasSelectedIncidents = selectedCount > 0;
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchReports(this.searchQuery, this.currentPage);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchReports(this.searchQuery, this.currentPage);
    }
  }

  // Download all attachments from popup (receives array of file objects)
  onDownloadAllIncidentAttachments(incident: any): void {
    if (!incident || !incident.attachments || incident.attachments.length === 0) {
      this.showInfoMessage('Info', 'No attachments available to download.');
      return;
    }
    // For now, simple iteration opening each in new tab if URL present; adapt when real API
    incident.attachments.forEach((att: any) => {
      const url = att.url || att.attachmentUrl || att.path;
      if (url) {
        window.open(url, '_blank');
      }
    });
    this.showSuccessMessage('Success', 'Attachments downloaded successfully!');
  }

  // Export incident details as Excel (stub - integrate with real export service if available)
  // onDownloadIncidentExcel(eventData: any): void {
  //   console.log('Excel export requested for incident:', eventData);
  //   // Placeholder: convert to CSV and trigger download
  //   const rows = [
  //     ['Issue No', eventData.issueNo],
  //     ['Title', eventData.title],
  //     ['Description', eventData.description],
  //     ['Selection Summary', eventData.selectionSummary || '']
  //   ];
  //   const csv = rows.map(r => r.map(v => '"' + (v ?? '') + '"').join(',')).join('\n');
  //   const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  //   const a = document.createElement('a');
  //   const url = URL.createObjectURL(blob);
  //   a.href = url;
  //   a.download = `incident_${eventData.issueNo || 'report'}.csv`;
  //   a.click();
  //   URL.revokeObjectURL(url);
  //   // this.showSuccessMessage('Success', 'Report exported successfully!');
  // }

  downloadAttachment(incident: any): void {
    if (!incident || !incident.issueNo) {
      this.showErrorMessage('Error', 'No incident number available for download.');
      return;
    }

    this.isLoading = true;
    
    this.reportService.downloadReportIssue(incident.issueNo).pipe(
      finalize(() => this.loaderService.hideLoader())
    ).subscribe({
      next: (blob: Blob) => {
        if (blob.size === 0) {
          this.showInfoMessage('Info', 'No files found for this incident.');
          return;
        }

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Explorer+_inc${incident.issueNo}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // this.showSuccessMessage('Success', 'Report files downloaded successfully!');
      },
      error: (error) => {
        console.error('Download error:', error);
        this.showErrorMessage('Error', 'Failed to download report files. Please try again.');
      }
    });
  }

  // 🔹 Update report (status or assignedTo)
  updateReport(incident: any): void {
    if (!incident || !incident.id) {
      console.error('Missing real ID for update:', incident);
      return;
    }

    // Get current user email from AuthenticationService
    let currentUserEmail = 'Unknown';
    const userData = this.authenticationService.GetuserData();
    if (userData && userData.email) {
      currentUserEmail = userData.email;
    }
    const payload = {
      id: incident.id, // ✅ real string ID
      status: incident.status,
      assignedTo: incident.assignedTo,
      assignedBy: currentUserEmail // Set to current user's email
    };

    console.log('Updating report with ID:', payload.id);

    this.reportService.updateReport(payload).subscribe({
      next: (res) => {
        console.log('Report updated successfully:', res);
        // Update previousAssignedTo after successful update
        if (incident) {
          incident.previousAssignedTo = incident.assignedTo;
        }
        this.showSuccessMessage('Success', 'Report updated successfully!');
      },
      error: (err) => {
        console.error('Error updating report:', err);
        this.showErrorMessage('Error', 'Failed to update report.');
      }
    });
  }

  // 🔹 Update report and refresh filter options
  updateReportAndRefreshFilters(incident: any): void {
    if (!incident || !incident.id) {
      console.error('Missing real ID for update:', incident);
      return;
    }

    // Get current user email from AuthenticationService
    let currentUserEmail = 'Unknown';
    const userData = this.authenticationService.GetuserData();
    if (userData && userData.email) {
      currentUserEmail = userData.email;
    }
    const payload = {
      id: incident.id, // ✅ real string ID
      status: incident.status,
      assignedTo: incident.assignedTo,
      assignedBy: currentUserEmail // Set to current user's email
    };

    console.log('Updating report with ID:', payload.id);

    this.reportService.updateReport(payload).subscribe({
      next: (res) => {
        console.log('Report updated successfully:', res);
        // Update previousAssignedTo after successful update
        if (incident) {
          incident.previousAssignedTo = incident.assignedTo;
        }
    setTimeout(() => {
        this.loadFilterOptions();
    }, 1000); 
        this.showSuccessMessage('Success', 'Report updated successfully!');
        
       
      },
      error: (err) => {
        console.error('Error updating report:', err);
        this.showErrorMessage('Error', 'Failed to update report.');
      }
    });
  }

  // 🔹 Triggered when status dropdown changes
  onStatusChange(incident: any): void {
    console.log('Status changed for:', incident.id, '→', incident.status);
    this.updateReportAndRefreshFilters(incident);
  }

  // Track previous assignedTo on dropdown open
  onAssignedToFocus(incident: any): void {
    incident.previousAssignedTo = incident.assignedTo;
  }

  // Only call updateReport if assignedTo value actually changed
  onAssignedToChange(incident: any): void {
    if (incident.previousAssignedTo === incident.assignedTo) {
      // No change, do not call API
      return;
    }
    
    // Don't call update if assigned to is empty
    if (!incident.assignedTo || incident.assignedTo.trim() === '') {
      console.log('Assigned to is empty, skipping update for:', incident.id);
      return;
    }
    
    console.log('Assigned to changed for:', incident.id, '→', incident.assignedTo);
    this.updateReportAndRefreshFilters(incident);
  }
  deleteSelectedIncidents(): void {
  const selected = this.filteredIncidents.filter((x) => x.isSelected);
  if (selected.length === 0) return;

  // Show confirmation popup instead of browser confirm
  this.alertService.showAlert({
    show: true,
    header: 'Confirm Deletion',
    message: `Are you sure you want to delete ${selected.length} incident(s)?`,
    confirm: true,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    onConfirm: () => this.executeIncidentDeletion(selected),
    onCancel: () => { /* no-op */ }
  } as any);
}

private executeIncidentDeletion(selected: any[]): void {
  let deletedCount = 0;
  const totalToDelete = selected.length;

  selected.forEach((incident) => {
    const body = { id: incident.id, isDeleted: true };

    this.reportService.updateReport(body).subscribe({
      next: () => {
        this.incidents = this.incidents.filter((x) => x.id !== incident.id);
        this.filteredIncidents = this.filteredIncidents.filter((x) => x.id !== incident.id);
        deletedCount++;

        if (deletedCount === totalToDelete) {
          this.totalRecords = Math.max(0, this.totalRecords - totalToDelete);
          this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
          if (this.filteredIncidents.length < this.pageSize && this.currentPage < this.totalPages) {
            this.currentPage++;
            this.fetchReports(this.searchQuery, this.currentPage, true);
          } else if (this.currentPage > this.totalPages && this.totalPages > 0) {
            this.currentPage = this.totalPages;
            this.fetchReports(this.searchQuery, this.currentPage, false);
          }
          this.showSuccessMessage('Success', `${totalToDelete} incident(s) deleted successfully!`);
        }
        this.onIncidentSelectionChange();
      },
      error: (err) => {
        console.error('Error deleting incident:', err);
        this.showErrorMessage('Error', 'Failed to delete incident: ' + (err?.message || 'Unknown error'));
        deletedCount++;
      }
    });
  });
}

  // 🔹 Export Incidents to Excel
  exportIncidentsToExcel(): void {
    this.isLoading = true;
    
    this.reportService.exportIncidentsToExcel(true).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Explorer+_IncidentReports.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.isLoading = false;
        // this.showSuccessMessage('Success', 'Export completed successfully!');
      },
      error: (error) => {
        console.error('Error exporting incidents:', error);
        this.isLoading = false;
        this.showErrorMessage('Error', 'Failed to export incidents: ' + (error?.message || 'Unknown error'));
      }
    });
  }

  // 🔹 Multi-select filter methods
  
  // Module filter methods
  toggleModuleDropdown(): void {
    this.showModuleDropdown = !this.showModuleDropdown;
    this.showStatusDropdown = false;
    this.showAssigneeDropdown = false;
  }

  toggleModuleSelection(module: string): void {
    const index = this.selectedModules.indexOf(module);
    if (index > -1) {
      this.selectedModules.splice(index, 1);
    } else {
      this.selectedModules.push(module);
    }
    this.onFiltersChange();
  }

  isModuleSelected(module: string): boolean {
    return this.selectedModules.includes(module);
  }

  clearModuleFilter(): void {
  this.selectedModules = [];
  this.onFiltersChange();
  }

  // Status filter methods
  toggleStatusDropdown(): void {
    this.showStatusDropdown = !this.showStatusDropdown;
    this.showModuleDropdown = false;
    this.showAssigneeDropdown = false;
  }
  toggleStatusSelection(status: string): void {
    const index = this.selectedStatuses.indexOf(status);
    if (index > -1) {
      this.selectedStatuses.splice(index, 1);
    } else {
      this.selectedStatuses.push(status);
    }
    this.onFiltersChange();
  }

  isStatusSelected(status: string): boolean {
    return this.selectedStatuses.includes(status);
  }

  clearStatusFilter(): void {
  this.selectedStatuses = [];
  this.onFiltersChange();
  }

  // Assignee filter methods
  toggleAssigneeDropdown(): void {
    this.showAssigneeDropdown = !this.showAssigneeDropdown;
    this.showModuleDropdown = false;
    this.showStatusDropdown = false;
  }

  toggleAssigneeSelection(assignee: string): void {
    const index = this.selectedAssignees.indexOf(assignee);
    if (index > -1) {
      this.selectedAssignees.splice(index, 1);
    } else {
      this.selectedAssignees.push(assignee);
    }
    this.onFiltersChange();
  }

  isAssigneeSelected(assignee: string): boolean {
    return this.selectedAssignees.includes(assignee);
  }

  clearAssigneeFilter(): void {
    this.selectedAssignees = [];
    this.onFiltersChange();
  }
  showDatePicker = false;

  // Close all dropdowns when clicking outside
  closeAllDropdowns(): void {
    this.showModuleDropdown = false;
    this.showStatusDropdown = false;
    this.showAssigneeDropdown = false;
    this.showDatePicker = false;
  }

  // Apply filters and refresh data
  onFiltersChange(): void {
    this.currentPage = 1; // Reset to first page when filters change
    this.fetchReports(this.searchQuery);
    
  }

  // Clear all filters
  clearAllFilters(): void {
    this.selectedModules = [];
    this.selectedStatuses = [];
    this.selectedAssignees = [];
    this.searchQuery = '';
    this.onFiltersChange();
  }

  // Helper method to format module names for display
  formatModuleForDisplay(module: string): string {
    if (!module) return module;
    // Convert "SNAPSHOT" to "Snapshot", "BRAND ANALYSIS" to "Brand Analysis", etc.
    return module.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  // Get filter summary text
  getModuleFilterText(): string {
    if (this.selectedModules.length === 0) return 'All Modules';
    if (this.selectedModules.length === 1) return this.formatModuleForDisplay(this.selectedModules[0]);
    return `${this.selectedModules.length} modules selected`;
  }

  getStatusFilterText(): string {
    if (this.selectedStatuses.length === 0) return 'All Statuses';
    if (this.selectedStatuses.length === 1) return this.selectedStatuses[0];
    return `${this.selectedStatuses.length} statuses selected`;
  }

  getAssigneeFilterText(): string {
    if (this.selectedAssignees.length === 0) return 'All Assignees';
    if (this.selectedAssignees.length === 1) return this.selectedAssignees[0];
    return `${this.selectedAssignees.length} assignees selected`;
  }

  // Notification helper methods
  showSuccessMessage(title: string, message: string): void {
    this.alertService.showAlert({
      show: true,
      header: title,
      message: message
    });
  }

  showErrorMessage(title: string, message: string): void {
    this.alertService.showAlert({
      show: true,
      header: title,
      message: message
    });
  }

  showInfoMessage(title: string, message: string): void {
    this.alertService.showAlert({
      show: true,
      header: title,
      message: message
    });
  }
  startDate: string | null = null;
endDate: string | null = null;

onDateRangeChange() {
  // Validate dates
  if (this.startDate && this.endDate && this.startDate > this.endDate) {
    // Reset end date if it's before start date
    this.endDate = null;
    this.showErrorMessage('Invalid Date Range', 'End date cannot be before start date');
    return;
  }

  console.log("Start:", this.startDate, "End:", this.endDate);
  
  // Trigger API call with updated date range
  this.onFiltersChange();
}

clearDateRange() {
  this.startDate = null;
  this.endDate = null;
  this.onFiltersChange();
}
 /** Determine pageSize by viewport width (adjust breakpoints/values as needed) */
  private getPageSizeForWidth(width: number): number {
    if (width >= 1536) return 25;
    if (width >= 1728) return 30;
    if (width >= 1920) return 35;
    return 10;
  }


}
